/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-disable no-continue */

import { addDays, diffDays, getDayOfWeek, maxDayDate } from '../day-date';
import DayInterval, { isValid } from '../day-interval/day-interval';
import * as DayIntervalGenerator from '../day-interval/day-interval-generator';
import * as GeneratorUtil from '../generator/generator-util';
import Stringify from '../stringify';

/**
 * @private
 * @typedef {import('../day-date').DayDate} DayDate
 * @typedef {import('../day-interval/day-interval-generator').DayIntervalGeneratorTransformer} DayIntervalGeneratorTransformer
 * @typedef {import('../day-of-week').SomeDaysOfWeek} SomeDaysOfWeek
 * @typedef {import('./holiday-interval').default} HolidayInterval
 */

/**
 * Zkontroluje povolený rozsah {@link maxDays}, viz důkaz v dokumentaci
 * konstanty `ReplacerPower.maxDays`.
 * @param {number} maxDays
 * @throws {RangeError}
 */
export function checkMaxDays(maxDays) {
  if (
    !(maxDays === Infinity || (Number.isSafeInteger(maxDays) && maxDays >= 7))
  ) {
    throw new RangeError(
      `maxDays=${maxDays}, but 7 <= maxDays <= Number.MAX_SAFE_INTEGER || maxDays === Infinity`
    );
  }
}

// TODO: Nápad, že by pro každý generátor HolidayInterval bylo účelné
// z hlediska výkonu "předskipovat" intervaly zaručeně v minulosti.
// Jenže naivní přístup přeskákání end v minulosti vlastně ani nemůže
// fungovat, protože current interval může mít začátek klidně 10 let
// do minulosti a tím pádem pro nespojení se nestačí dívat, že máme mezeru
// alespoň jeden den, ale tento interval hledání do minulosti by musel
// být daleko větší!
//
// Teoreticky by šlo ošetřit tím, že by se zjistila maximální délka všech
// intervalů (což se komplikuje přestupnými roky a rozšiřováním na obě strany,
// ale dalo by se přičíst 1+2*6 dní pro jistotu) a rozšířit hledání konce,
// že musí být od today alespoň tolik dní. Ale to je asi zbytečné, komplikované
// a náchylné na chyby...
/**
 * Generátor roustoucích, nepřekrývajích a nenavazujících maximálně sloučených
 * minimálních intervalů začínajích a končících na on dni. Pokud při spojování
 * dojde k překročení maximální délky intervalu {@link maxDays}, tak se od dne
 * po dnu konci intervalu hledá první umělý on den vlevo, který musí přidadat
 * na skutečný on den v týdnu.
 * @param {HolidayInterval[]} intervals Prázdninové intervaly.
 * @param {SomeDaysOfWeek} onDays Sjednocení on dnů v týdnu.
 * @param {number} maxDays Maximální počet dní pro sekání na kratší intervaly.
 * Celé číslo v rozsahu [7, Number.MAX_SAFE_INTEGER] nebo Infinity.
 *
 * Pozor, že při použití Infinity a dosažení bodu singularity (tj. roku, který
 * je úplně pokrytý opakovanými intervaly) se může pro nalezení jednoho intervalu
 * iterovat do praktického nekonečna po roce, což může zabrat až desítky sekund!
 * @param {Object} [opts = {}]
 * @param {(...data: any[]) => void} [opts.logger] Logger pro debugování.
 * @returns {Generator<DayInterval, void, undefined>}
 * @throws {RangeError} Pokud {@link maxDays} není v daném rozsahu.
 */
export default function holidayGenerator(
  intervals,
  onDays,
  maxDays,
  { logger } = {}
) {
  checkMaxDays(maxDays);

  // Logy pro debugovací účely.
  const logEnabled = logger !== undefined;
  /** @type {(name: string) => [DayIntervalGeneratorTransformer] | []} */
  const logTransformers = logEnabled
    ? (name) => [GeneratorUtil.log(logger, name)]
    : () => [];

  // TODO: Výpis parametrů generátoru, nyní zatím tak nějak nahrubo.
  if (logEnabled) {
    logger(Stringify.call('holidayGenerator', intervals, onDays, maxDays));
  }

  return GeneratorUtil.compose(
    GeneratorUtil.monotonicMerge(
      // Komparační funkce, měla by řadit podle `start` vzestupně a podle `end`
      // sestupně. Otočené řazení podle `end` je z důvodu, aby se při stejném
      // `start` vybral nejprve co největší interval, jelikož nonOverlapping
      // generátor tímto pokryje co nejvíce intervalů, které se přeskočí a tím
      // potenciálně ušetří práci mergeAndSplitMaxDays.
      // TODO: Po zapnutí logování je log ze stávajících testů stejný pro end
      // sestupné i vzestupné a časy +/- stejné. Takže hypotéza výše se (zatím)
      // nepotrvdila, ale třeba jen nemáme ty správné testy...
      (a, b) => diffDays(a.start, b.start) || diffDays(b.end, a.end),
      // Z HolidayIntervalů je nutné získat generátory, které se ale musí
      // předzpracovat tak, aby mohlo dojít ke sloučení navazujících intervalů
      // s off dny.
      ...intervals.map((interval, i) =>
        GeneratorUtil.compose(
          interval.nonIcalGenerator(),
          ...logTransformers(`    nonIcal ${String(i).padStart(4, ' ')}`),
          preprocessGenerator(onDays),
          ...logTransformers(`   preprocess ${String(i).padStart(2, ' ')}`)
        )
      )
    ),
    ...logTransformers('  monotonicMerge'),
    DayIntervalGenerator.nonOverlapping,
    ...logTransformers(' nonOverlapping '),
    mergeAndSplitMaxDays(onDays, maxDays),
    ...logTransformers('mergeAndSplit   ')
  );
}
// TODO: Možná aby to bylo obecné (a mohlo být v DayIntervalGenerator) by chtělo
// mít nějakou volitelnou funkci, která by brala stávající délku intervalu a sama
// stanovovala rozdělení na kratší, tj. end stávajícího a případný začátek zbytku,
// pokud nějaký zbyl.
/**
 * Transformační generátor roustoucích, nepřekrývajích se a nenavazujících
 * maximálně sloučených minimálních intervalů začínajích a končících na on dni,
 * který zpracovává roustoucí a nepřekrývající se intervaly.
 *
 * Pokud není {@link maxDays} Infinity, tak výstupní sloučené intervaly případně
 * delší jak {@link maxDays} budou iterativně od začátku rozděleny na menší
 * intervaly o maximální délce {@link maxDays}. Při hledání umělého dne
 * přerušení se konzultují {@link onDays} a hledá se nejbližší levý on den.
 * @param {import('../day-of-week').SomeDaysOfWeek} onDays On dny v týdnu.
 * Slouží k získání minimálního intervalu (začátek a konec na on dni) a hledání
 * umělého on dne.
 * @param {number} maxDays Maximální počet dní pro sekání na kratší intervaly.
 * Celé číslo v rozsahu [7, Number.MAX_SAFE_INTEGER] nebo Infinity.
 *
 * Pozor, že při použití Infinity a dosažení bodu singularity (tj. roku, který
 * je úplně pokrytý opakovanými intervaly) se může pro nalezení jednoho intervalu
 * iterovat do praktického nekonečna po roce, což může zabrat až desítky sekund!
 * @returns {DayIntervalGeneratorTransformer}
 * @throws {RangeError} Pokud {@link maxDays} není v daném rozsahu.
 */
function mergeAndSplitMaxDays(onDays, maxDays) {
  checkMaxDays(maxDays);
  const mergeDays = maxDays + 6;
  // eslint-disable-next-line max-statements
  return function* (muggleIterator) {
    const iterator = GeneratorUtil.oracle(muggleIterator);
    let current = iterator.next();
    while (!current.done) {
      // Fáze spojování

      // Kvůli preprocessingu může být vrácený interval v nejhorším případě
      // delší až o 6 off dní než je skutečná délka minimálního intervalu (musí
      // začínat a končit na on dnu). Abychom nemuseli v každé smyčce kandidátní
      // interval postprocessovat (zkracovat o off dny zprava), tak raději
      // děláme test na zvětšenou délku a případný interval se skutečnou délkou
      // větší než maxDays jednotně zpracujeme ve split fázi níže.
      const currentDays = current.value.days;
      if (currentDays <= mergeDays) {
        // Pokud je délka stávajícího intervalu menší nebo rovna mergeDays,
        // tak musíme případný další interval spojit, je-li navazující.
        //
        // Navazující interval je právě tehdy, když start dalšího je den
        // po end stávajícího, tj. days === 1.
        //
        // Pokud by následující interval nebyl navazující, tak ho nesmíme slepě
        // vytáhnout z iterátoru, protože stávající interval se může rozdělit
        // iterativně přes více průchodů smyčkou na více intervalů a tudíž by
        // bylo složité ho uchovávat. Proto použijeme iterátor s orákulem, kde
        // můžeme nahlížet na další hodnotu, aniž bychom ji vyčetli.
        const nextOracle = iterator.nextOracle;
        if (!nextOracle.done) {
          const offset = diffDays(nextOracle.value.start, current.value.end);
          // TODO: Možná zbytečná runtime kontrola, že intervaly
          // jsou nepřekrývající se (což je invariant daný dokumentací).
          // if (offset < 1) {
          //   throw new Error(
          //     `overlapping intervals ${current.value} ${nextOracle.value}`
          //   );
          // }

          // Stačil by test offset === 1, ale pro robustnost a bez případné
          // runtime kontroly výše je lepší případné překrytí (které by nemělo
          // nastat) tolerovat než zcela chybně interval nespojit.
          if (offset <= 1) {
            // Navazující interval, prodloužíme stávající podle konce dalšího.
            current.value = current.value.setEnd(nextOracle.value.end);
            // Výše jsme udělali jen peek, takže nyní po zpracování musíme
            // interval skutečně vyčíst, aby se chybně nezpracoval 2x.
            iterator.next();
            continue;
          }
        }
      }

      // Fáze případného iterativního dělení na délku maximálně maxDays

      // Stávající ořezaný interval je kratší nebo roven maxDays.
      let head = postprocessInterval(onDays, current.value);
      if (head.days <= maxDays) {
        yield head;
        current = iterator.next();
        continue;
      }

      // Hledání umělého on dne.
      /** @type {DayDate} */
      let onDay;
      try {
        // Inicializujeme den po délce maxDays od počátku intervalu. Konec se
        // vypočte jako maxDay - 1, takže pro den po end se jedničky vyruší.
        onDay = addDays(current.value.start, maxDays);
      } catch {
        // Výpočet addDays může vrhnout, ale kvůli kladnému maxDays to nastane
        // jen když by onDay bylo za rozsahem DayDate, proto saturujeme
        // na maxDayDate.
        onDay = maxDayDate;
      }
      // Hledáme couváním do minulosti, dokud nenalezneme on den podle onDays.
      //
      // Pozor, že pokud by nebyl minimální limit na maxDays, taky bychom
      // mohli vyrobit nevalidní interval! Couvnout zpět můžeme až o 6 dní.
      //
      // Pokud bychom měli start 0100-01-01 a přičetli minimum 7 dní,
      // tak máme 0100-01-08 a couvnutím o max. 6 dní se nejhůř dostaneme
      // na 0100-01-02, takže addDays nemůže podtéct a vrhnout. QED
      while (!onDays.get(getDayOfWeek(onDay))) {
        onDay = addDays(onDay, -1);
      }

      // Head interval - end musí být den před umělým on dnem.
      // Aby byl interval minimální, musí se případné off dny zprava ořezat.
      head = postprocessInterval(
        onDays,
        // Platí důkaz viz výše, onDay může být nejhůř 0100-01-02, takže
        // couvnutím o den se dostaneme maximálně na počátek věků 0100-01-01,
        // takže nemůže vrhnout.
        head.setEnd(addDays(onDay, -1))
      );
      yield head;

      // Zpracování zbytku intervalu. Teoreticky den po on dnu může s původním
      // end vytvořit nevalidní interval. S tím si ale `preprocessMaybeInterval`
      // poradí a navíc dělá předzpracování `start` tak, aby začínal na on dnu.
      // Proměnná current.value.end obsahuje původní konec rozšířený o off dny
      // (a nikoliv ořezaný), takže se end neposune (jen zkontroluje).
      //
      // Zachování původního end (případně maximálně rozšířeného o off dny) je
      // stěžejní, protože případný krátký zbytek ještě může případně navázat
      // s dalším navazujícím intervalem v další iteraci!
      //
      // Pokud umělý on den připadá na konec věků, tak pak by případný zbytek
      // intervalu začínal za koncem věků a addDays by vrhlo.
      // TODO: Provnávat pomocí (DayDate) isEqual?
      if (onDay !== maxDayDate) {
        const tail = preprocessMaybeInterval(onDays, {
          start: addDays(onDay, 1),
          end: current.value.end
        });
        if (tail) {
          // Pokud máme nějaký validní zbytek, tak uložíme pro další iteraci.
          current.value = tail;
          continue;
        }
      }

      // Pokud zbytek nemáme, tak je nutné získat a uložit další interval.
      current = iterator.next();
    }
  };
}

/**
 * Zpracuje konec intervalu tak, aby to byl minimální prázdninový interval
 * (začíná a končil vždy on dnem, tj. zbytečně nepokrývá off dny).
 *
 * Funguje v tandemu s {@link preprocessMaybeInterval}, kdy revertuje rozšíření
 * intervalu o případné off dny do budoucna, což nutné pro slučitelnost,
 * ale naopak pro minimálnost intervalu to vadí.
 * @param {SomeDaysOfWeek} onDays
 * @param {DayInterval} interval Vstupní interval, pro který platí
 * `onDays.get(dayOfWeek(start))`.
 * @returns {DayInterval} Zpracovaný interval (jehož délka je menší nebo stejná
 * jako u vstupu), pro který platí `onDays.get(dayOfWeek(start))`.
 */
function postprocessInterval(onDays, interval) {
  let { start, end } = interval;

  // TODO: Zbytečná runtime kontrola, že začátek intervalu je on den.
  // if (!onDays.get(getDayOfWeek(start))) {
  //   throw new TypeError('start of interval is not on day');
  // }

  // Hledání nejbližšího on dne do minulosti pro konec intervalu.
  // Pokud je interval validní a je to on den (ověřeno výše a zajišťuje to
  // preprocessGenerator a mergeAndSplitMaxDays), tak pak v nejhorším
  // případě se může couvnout nejdál na start, což by byl validní
  // jednodenní interval.
  while (!onDays.get(getDayOfWeek(end))) {
    end = addDays(end, -1);
  }

  return DayInterval.from(start, end);
}

/**
 * Zpracuje možná validní interval (nevalidnost se připouští pro zjednodušení
 * volajícího {@link mergeAndSplitMaxDays}) pro slučitelnost, tj. {@link start}
 * se posune na nejbližší budoucí on den (a pokud návratová hodnota není
 * `undefined`, tak platí `onDays.get(dayOfWeek(start))`) a {@link end}
 * se případně maximálně rozšíří o přiléhající off dny, pokud to byl poslední
 * on den v řadě.
 * @param {SomeDaysOfWeek} onDays
 * @param {{ start: DayDate, end: DayDate }} maybeInterval Jako DayInterval,
 * který může mít případně prohozený start a end, tj. být i nevalidní.
 * @returns {DayInterval | undefined} Zpracovaný interval nebo `undefined`,
 * pokud je po zpracování nevalidní (prohozený start/end nebo by se zpracováním
 * start dostal mimo rozsah {@link DayDate}).
 */
function preprocessMaybeInterval(onDays, maybeInterval) {
  let { start, end } = maybeInterval;

  const isInvalid = () => !isValid(start, end);

  // Mějme nejhorší případ, že start je on den a end je den před start. Start
  // se zpracováním neposune a zpracování end se posune na start den (je on)
  // a couvne, tj. nic se nezmění a interval stále bude nevalidní. Pokud by ale
  // start nebyl na on dnu, tak se sice start posune, ale opět end ho může
  // v nejhorším případě akorát dohnat a pak couvne zpět na off den.
  // Tím pádem se nevalidní interval nemůže nikdy zpracováním zvalidnit
  // a můžeme si dovolit tuto zkratku. QED
  if (isInvalid()) {
    return;
  }

  // Hledání nejbližšího budoucího on dne pro začátek intervalu.
  //
  // Pokud dojde k výjimce, tak další on den leží za rozsahem DayDate,
  // a proto předčasně interval vyfiltrujeme, jelikož platí podmínka,
  // že start musí být on den.
  try {
    while (!onDays.get(getDayOfWeek(start))) {
      start = addDays(start, 1);
    }
  } catch (error) {
    return;
  }

  if (isInvalid()) {
    return;
  }

  // Maximálně rozšíření konce intervalu o přiléhající off dny, pokud to byl
  // poslední on den v řadě. Tzn. hledá se první on den po end+1 dnu a pak
  // se o den couvne.
  //
  // Pokud dojde k výjimce, tak end muselo být jen a pouze konec věků.
  // Pokud to byl off den, tak se nemůžeme podívat, jestli by následující den
  // byl online (a v tom případě bychom se museli stejně vrátit o den zpět
  // na poslední off den v řadě) nebo offline (a standardně pokračovali dál).
  // Pokud to byl on den, tak podobně nevíme, zda je to poslední on den v řadě
  // (a mělo by se rozšiřovat o off dny, ale nemáme kam) anebo bude následovat
  // další on den (a v tom případě nerozšiřujeme). Takže v každém případě
  // ponecháme end na konci věků a není to chybný stav.
  try {
    do {
      end = addDays(end, 1);
    } while (!onDays.get(getDayOfWeek(end)));
    end = addDays(end, -1);
  } catch (error) {}

  if (isInvalid()) {
    return;
  }

  return DayInterval.from(start, end);
}

/**
 * Transformační generátor předzpracovávající (a potenciálně filtrující)
 * intervaly pro spojitelnost. Tj. {@link start} se posune na nejbližší budoucí
 * on den a {@link end} se případně maximálně rozšíří o přiléhající off dny,
 * pokud to byl poslední on den v řadě.
 *
 * Zachovává monotónnost a pro každý interval na výstupu platí
 * `onDays.get(dayOfWeek(start))`.
 * @param {SomeDaysOfWeek} onDays
 * @returns {DayIntervalGeneratorTransformer}
 */
function preprocessGenerator(onDays) {
  return function* (iterator) {
    for (const interval of iterator) {
      const processedInterval = preprocessMaybeInterval(onDays, interval);
      if (processedInterval) {
        yield processedInterval;
      }
    }
  };
}

export const internal = {
  postprocessInterval,
  preprocessMaybeInterval
};
